import { haversineM, isLngLat } from "./geo.ts";
import { simplifyToMax } from "./editor-geom.ts";
import type { LngLat } from "./types.ts";

function finiteCoord(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Same cap the old nearby Piece list used. */
export const NEARBY_QUEST_LIMIT = 50;
/** Map preview lines stay light; the full route loads with the quest detail. */
export const PREVIEW_MAX_POINTS = 160;
const PREVIEW_TOLERANCE_M = 4;

export interface BBox {
  minLat: number;
  minLng: number;
  maxLat: number;
  maxLng: number;
}

/** Gameplay Start for detail markers and routing; not the home-map list pin. */
export function questStart(start: LngLat | null, line: LngLat[] | null, center: LngLat): LngLat {
  if (start) return start;
  if (line && line.length > 0) return line[0]!;
  return center;
}

/** Center of a route line's lng/lat bounds — home map pin for any quest with a route. */
export function routeLineBBoxCenter(line: readonly LngLat[]): LngLat {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const [lng, lat] of line) {
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  }
  return [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
}

/** Drop null/NaN points so MapLibre never sees invalid GeoJSON during list previews. */
export function sanitizeRouteCoordinates(coords: readonly unknown[]): LngLat[] {
  const out: LngLat[] = [];
  for (const p of coords) {
    if (!Array.isArray(p) || p.length < 2) continue;
    const lng = finiteCoord(p[0]);
    const lat = finiteCoord(p[1]);
    if (lng != null && lat != null) out.push([lng, lat]);
  }
  return out;
}

/** Full route on a quest detail, else a list preview line. */
export function questRouteLineForMap(quest: {
  route?: { coordinates?: LngLat[] | null } | null;
  route_preview?: { coordinates?: LngLat[] | null } | null;
}): LngLat[] | null {
  const coords = quest.route?.coordinates;
  if (Array.isArray(coords) && coords.length >= 2) {
    const line = sanitizeRouteCoordinates(coords);
    if (line.length >= 2) return line;
  }
  const preview = quest.route_preview?.coordinates;
  if (Array.isArray(preview) && preview.length >= 2) {
    const line = sanitizeRouteCoordinates(preview);
    if (line.length >= 2) return line;
  }
  return null;
}

/** Route polyline for home map list overlays (preview first, then full route). */
export function questListRouteCoordinates(quest: {
  route?: { coordinates?: LngLat[] | null } | null;
  route_preview?: { coordinates?: LngLat[] | null } | null;
}): LngLat[] | null {
  return questRouteLineForMap(quest);
}

/** True when a home list card has a drawable route preview line. */
export function hasListRoutePreview(quest: {
  route_preview?: { coordinates?: LngLat[] | null } | null;
}): boolean {
  return (quest.route_preview?.coordinates?.length ?? 0) >= 2;
}

export function isSpotTraceListQuest(quest: { quest_type?: string | null }): boolean {
  return quest.quest_type === "spot_trace";
}

/** Spot-trace list cards use photo DOM markers, not the connector route line, for thumbnails. */
export function listThumbnailUsesSpotMarkers(quest: {
  quest_type?: string | null;
  spot_count?: number | null;
  spots_preview?: ReadonlyArray<{ lat?: unknown; lng?: unknown }> | null;
  route_preview?: { coordinates?: LngLat[] | null } | null;
}): boolean {
  if (!isSpotTraceListQuest(quest)) return false;
  const spots = spotCoordinates(quest.spots_preview);
  const count = spots.length || (quest.spot_count ?? 0);
  if (count < 1) return false;
  if (hasListRoutePreview(quest)) {
    return spots.length > 0 && spots.length <= 3;
  }
  return true;
}

/** Route thumbnail or spot-marker preview for a home list card. */
export function hasListCardPreview(quest: {
  quest_type?: string | null;
  route_preview?: { coordinates?: LngLat[] | null } | null;
  spot_count?: number | null;
  spots_preview?: ReadonlyArray<{ lat?: unknown; lng?: unknown }> | null;
}): boolean {
  if (listThumbnailUsesSpotMarkers(quest)) {
    return spotCoordinates(quest.spots_preview).length >= 1 || (quest.spot_count ?? 0) > 0;
  }
  return hasListRoutePreview(quest);
}

/** Quest types that render the Route Trace read-only page (home inline panel and /q). */
export function usesQuestPageTemplate(q: {
  quest_type?: string | null;
  route?: { coordinates?: unknown[] } | null;
  checkpoints?: unknown[] | null;
}): boolean {
  if (q.quest_type === "spot_trace") return (q.checkpoints?.length ?? 0) >= 1;
  if (q.quest_type === "route_trace") return true;
  return (q.route?.coordinates?.length ?? 0) >= 2;
}

/** Spot-only nearby/saved card (spots summary, no route preview line). */
export function isSpotOnlyListQuest(quest: {
  route_preview?: { coordinates?: LngLat[] | null } | null;
  spot_count?: number | null;
  spots_preview?: ReadonlyArray<unknown> | null;
}): boolean {
  if (hasListRoutePreview(quest)) return false;
  return (quest.spot_count ?? 0) > 0 || (quest.spots_preview?.length ?? 0) > 0;
}

/** Route length for list meta and preview stats; 0 when there is no preview line. */
export function questListRouteLengthM(quest: { route_preview?: { length_m?: number } | null }): number {
  const m = quest.route_preview?.length_m;
  return typeof m === "number" && Number.isFinite(m) ? m : 0;
}

/** Start pin for map/list, matching the Worker nearby feed (route_trace may ignore the DB start). */
export function questStartSpotForMap(quest: {
  start?: { lat?: unknown; lng?: unknown } | null;
  checkpoints?: Array<{ kind?: string | null; lat?: unknown; lng?: unknown }> | null;
  quest_type?: string | null;
  start_fixed?: boolean;
}): LngLat | null {
  if ("start" in quest && quest.start && typeof quest.start === "object") {
    const lng = finiteCoord(quest.start.lng);
    const lat = finiteCoord(quest.start.lat);
    if (lng != null && lat != null) return [lng, lat];
  }
  const useCheckpoint = quest.quest_type !== "route_trace" || quest.start_fixed === true;
  if (useCheckpoint && Array.isArray(quest.checkpoints)) {
    const spot = quest.checkpoints.find((c) => c.kind === "start");
    if (spot) {
      const lng = finiteCoord(spot.lng);
      const lat = finiteCoord(spot.lat);
      if (lng != null && lat != null) return [lng, lat];
    }
  }
  return null;
}

/** Lng/lat pairs for spot-only home list pins and map framing. */
export function spotCoordinates(
  spots: ReadonlyArray<{ lat?: unknown; lng?: unknown }> | null | undefined,
): LngLat[] {
  if (!Array.isArray(spots)) return [];
  const out: LngLat[] = [];
  for (const s of spots) {
    const lng = finiteCoord(s.lng);
    const lat = finiteCoord(s.lat);
    if (lng != null && lat != null && isLngLat([lng, lat])) out.push([lng, lat]);
  }
  return out;
}

/** Map/list pin and list-card “away” anchor: route bbox center, else spots bbox center, else quest center. */
export function questMapPoint(quest: {
  center_lng?: unknown;
  center_lat?: unknown;
  route?: { coordinates?: LngLat[] | null } | null;
  route_preview?: { coordinates?: LngLat[] | null } | null;
  spots_preview?: ReadonlyArray<{ lat?: unknown; lng?: unknown }> | null;
  checkpoints?: ReadonlyArray<{ lat?: unknown; lng?: unknown }> | null;
}): LngLat | null {
  const line = questRouteLineForMap(quest);
  if (line && line.length >= 2) return routeLineBBoxCenter(line);
  const spotLine = spotCoordinates(quest.spots_preview ?? quest.checkpoints);
  if (spotLine.length > 0) return routeLineBBoxCenter(spotLine);
  const lng = finiteCoord(quest.center_lng);
  const lat = finiteCoord(quest.center_lat);
  if (lng != null && lat != null && isLngLat([lng, lat])) return [lng, lat];
  return null;
}

/**
 * Home lists and maps a quest only once it's published with a title. Drafts (often blank
 * ones left by New quest) and a quest's own Start/Finish spots never get a home entry.
 */
export function homeListed(q: { status: string; title?: string | null }): boolean {
  return q.status === "active" && (q.title ?? "").trim() !== "";
}

/**
 * My quests is only ever your own list, so it keeps drafts (blank ones included, shown as
 * Untitled) next to published quests, newest first. Hidden/removed stay out.
 */
export function myQuestsListed<T extends { status: string; created_at: string }>(quests: T[]): T[] {
  return quests
    .filter((q) => q.status === "active" || q.status === "draft")
    .sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0));
}

/**
 * Whether the home detail panel may show a quest. Anyone's published, titled quest with a
 * route opens; your own published quest opens even without a route or title, and your own
 * draft only when it came from the signed-in missions API (never a public read or cache).
 */
export function detailOpenable(
  q: {
    status: string;
    title?: string | null;
    mine?: boolean;
    route?: { coordinates: unknown[] } | null;
    checkpoints?: unknown[] | null;
  },
  opts: { ownerApi: boolean },
): boolean {
  if (homeListed(q) && (q.route?.coordinates.length ?? 0) >= 2) return true;
  if (homeListed(q) && (q.checkpoints?.length ?? 0) >= 1) return true;
  if (q.mine !== true) return false;
  return q.status === "active" || (opts.ownerApi && q.status === "draft");
}

export function routePreview(line: LngLat[]): LngLat[] {
  if (line.length <= PREVIEW_MAX_POINTS) return line;
  return simplifyToMax(line, PREVIEW_TOLERANCE_M, PREVIEW_MAX_POINTS);
}

export function bboxIntersects(a: BBox, b: BBox): boolean {
  return a.minLat <= b.maxLat && a.maxLat >= b.minLat && a.minLng <= b.maxLng && a.maxLng >= b.minLng;
}

/** User-to-pin distance for list meta (same point as the map pin); null when viewer or pin is unknown. */
export function questListDistanceM(
  item: { distance_m?: number; start?: { lat?: unknown; lng?: unknown } | null },
  viewer: { lat: number; lng: number } | null,
): number | null {
  if (!viewer || !isLngLat([viewer.lng, viewer.lat])) return null;
  const lat = typeof item.start?.lat === "number" ? item.start.lat : Number(item.start?.lat);
  const lng = typeof item.start?.lng === "number" ? item.start.lng : Number(item.start?.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng) && isLngLat([lng, lat])) {
    return Math.round(haversineM([viewer.lng, viewer.lat], [lng, lat]));
  }
  if (typeof item.distance_m === "number" && Number.isFinite(item.distance_m)) return item.distance_m;
  return null;
}

/** Nearest first; ties keep the newer quest first. */
export function byDistance<T extends { distance_m: number; created_at: string }>(a: T, b: T): number {
  if (a.distance_m !== b.distance_m) return a.distance_m - b.distance_m;
  return a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0;
}
